import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useSEO } from '../../../hooks/shared';
import { stateName } from '../../../lib/states';
import { voteAPI } from '../api/vote';

const TYPE_STYLES = {
  primary: 'bg-purple-100 text-purple-700',
  general: 'bg-primary-100 text-primary-700',
  special: 'bg-amber-100 text-amber-700',
  runoff: 'bg-orange-100 text-orange-700',
};

function daysUntil(date) {
  const diff = new Date(date).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function VoteState() {
  const { state } = useParams();
  const navigate = useNavigate();
  const stateUpper = state.toUpperCase();
  const fullName = stateName(stateUpper);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vote-state', state],
    queryFn: () => voteAPI.getState(state),
  });

  useSEO({
    title: `Voting in ${fullName}`,
    description: `Find election dates, polling locations, voter registration deadlines, and official resources for ${fullName}. County-level election info and official websites.`,
  });

  if (isLoading) {
    return (
      <div className="bg-neutral-50 min-h-screen py-8">
        <div className="container-custom max-w-4xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-neutral-200 rounded w-1/2" />
            <div className="h-4 bg-neutral-200 rounded w-1/3" />
            <div className="h-48 bg-white rounded-2xl border border-neutral-200" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data?.ok) {
    return (
      <div className="bg-neutral-50 min-h-screen py-8">
        <div className="container-custom max-w-4xl text-center py-16">
          <p className="text-neutral-500 mb-4">No voting information found for this state.</p>
          <Link to="/vote" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            Back to search
          </Link>
        </div>
      </div>
    );
  }

  const { stateAuthority, elections, countyAuthorities, pagination } = data;
  const nextElection = elections[0];

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      <div className="container-custom max-w-4xl">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm text-neutral-500 mb-6">
          <Link to="/vote" className="hover:text-neutral-700">
            Vote
          </Link>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-neutral-900 font-medium">{fullName}</span>
        </div>

        {/* State header */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm mb-6">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-neutral-900 mb-2">
            Voting in {fullName}
          </h1>
          <p className="text-neutral-600 mb-4">
            Election dates, official resources, and county-level voting info for {fullName}.
          </p>

          {/* Official links */}
          <div className="flex flex-wrap gap-3">
            {stateAuthority.websiteUrl && (
              <a
                href={stateAuthority.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Official State Election Website
              </a>
            )}
            {stateAuthority.calendarUrl &&
              stateAuthority.calendarUrl !== stateAuthority.websiteUrl && (
                <a
                  href={stateAuthority.calendarUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Election Calendar
                </a>
              )}
          </div>
        </div>

        {/* Upcoming elections */}
        {elections.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-serif font-bold text-neutral-900 mb-4">
              Upcoming Elections
            </h2>
            <div className="space-y-3">
              {elections.map((election) => {
                const days = daysUntil(election.electionDate);
                const type = (election.electionType || 'general').toLowerCase();
                return (
                  <div
                    key={election.id}
                    className="bg-white rounded-xl border border-neutral-200 p-5 hover:border-primary-200 transition-colors cursor-pointer"
                    onClick={() => navigate(`/elections/${election.id}`)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className={`px-2 py-0.5 text-xs font-semibold rounded-full ${TYPE_STYLES[type] || 'bg-neutral-100 text-neutral-600'}`}
                          >
                            {election.electionType || 'General'}
                          </span>
                          {days > 0 && days <= 30 && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-600">
                              {days} days away
                            </span>
                          )}
                          {days > 30 && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600">
                              {days} days away
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-neutral-900 mb-1">{election.name}</h3>
                        <p className="text-sm text-neutral-500">
                          {new Date(election.electionDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                      <svg
                        className="w-5 h-5 text-neutral-400 shrink-0 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>

                    {/* Key dates */}
                    {(election.registrationDeadline ||
                      election.earlyVotingStart ||
                      election.absenteeBallotDeadline) && (
                      <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-500">
                        {election.registrationDeadline && (
                          <span>
                            <span className="font-medium text-neutral-700">
                              Registration deadline:
                            </span>{' '}
                            {new Date(election.registrationDeadline).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        )}
                        {election.earlyVotingStart && (
                          <span>
                            <span className="font-medium text-neutral-700">Early voting:</span>{' '}
                            {new Date(election.earlyVotingStart).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                            {election.earlyVotingEnd &&
                              ` – ${new Date(election.earlyVotingEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                          </span>
                        )}
                        {election.absenteeBallotDeadline && (
                          <span>
                            <span className="font-medium text-neutral-700">Absentee deadline:</span>{' '}
                            {new Date(election.absenteeBallotDeadline).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {elections.length === 0 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center mb-6">
            <p className="text-neutral-500">No upcoming elections found for {fullName}.</p>
            <p className="text-sm text-neutral-400 mt-1">
              Check the official state website for the latest information.
            </p>
          </div>
        )}

        {/* Key dates highlight (from nearest election) */}
        {nextElection?.registrationDeadline && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="font-semibold text-amber-900">Registration Deadline</h3>
            </div>
            <p className="text-amber-800">
              Register by{' '}
              <span className="font-bold">
                {new Date(nextElection.registrationDeadline).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>{' '}
              for the {nextElection.name}.
            </p>
          </div>
        )}

        {/* County directory */}
        {countyAuthorities.length > 0 && (
          <div>
            <h2 className="text-xl font-serif font-bold text-neutral-900 mb-4">
              Counties in {fullName}
            </h2>
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
              <div className="divide-y divide-neutral-100">
                {countyAuthorities.map((c) => (
                  <Link
                    key={c.id}
                    to={`/vote/${state}/${c.slug}`}
                    className="flex items-center justify-between px-5 py-3.5 hover:bg-neutral-50 transition-colors"
                  >
                    <div>
                      <span className="font-medium text-neutral-900">{c.name}</span>
                      {c.websiteUrl && (
                        <span className="ml-2 text-xs text-neutral-400">
                          {new URL(c.websiteUrl).hostname}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {c.websiteStatus === 'active' && (
                        <span
                          className="w-2 h-2 bg-emerald-500 rounded-full"
                          title="Website verified"
                        />
                      )}
                      <svg
                        className="w-4 h-4 text-neutral-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
              {pagination.pages > 1 && (
                <div className="px-5 py-3 border-t border-neutral-100 text-sm text-neutral-500">
                  Showing {countyAuthorities.length} of {pagination.total} counties
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
